import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import {
  People as UsersIcon,
  Receipt as BillingIcon,
  Settings as ConfigIcon,
  Timeline as AnalyticsIcon,
  Assignment as AuditIcon
} from '@mui/icons-material';
import UserManagement from './UserManagement';
import BillingManagement from './BillingManagement';
import PricingConfiguration from './PricingConfiguration';
import AdminAnalytics from './AdminAnalytics';
import AuditLog from './AuditLog';
import NotificationSnackbar from '../common/NotificationSnackbar';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

function AdminDashboard() {
  const [currentTab, setCurrentTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    description: '',
    severity: 'success'
  });

  // Handle initial tab from URL - only on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1).toLowerCase();
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab')?.toLowerCase();

    const tabMap = {
      'users': 0,
      'billing': 1,
      'pricing': 2,
      'analytics': 3,
      'audit': 4
    };

    const initialTab = tabMap[hash] ?? tabMap[tabParam] ?? 0;
    setCurrentTab(initialTab);
  }, []); // Empty dependency array - only run on mount

  // Memoize tab change handler to prevent recreating on every render
  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
    const tabNames = ['users', 'billing', 'pricing', 'analytics', 'audit'];
    // Use replaceState instead of pushState to avoid browser history entries
    window.history.replaceState(null, '', `#${tabNames[newValue]}`);
  }, []);

  // Memoize notification handler to prevent recreating on every render
  const showNotification = useCallback((message, description, severity = 'success') => {
    setNotification({
      open: true,
      message,
      description,
      severity
    });
  }, []);

  const handleNotificationClose = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <Container maxWidth="lg" sx={{ flex: 1 }}>
        <h1 className="font-bold mb-8">EUDR API Administration</h1>
        
        <Box sx={{ width: '100%' }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
          }}>
            <Tabs 
              value={currentTab}
              onChange={handleTabChange}
              aria-label="admin dashboard tabs"
              textColor="primary"
              indicatorColor="primary"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              sx={{
                '.MuiTab-root': {
                  fontSize: isMobile ? '0.8rem' : 'inherit',
                  minWidth: isMobile ? 'auto' : 90,
                  px: isMobile ? 1.5 : 2,
                  minHeight: isMobile ? 48 : 56,
                }
              }}
            >
              <Tab 
                icon={<UsersIcon />}
                label={!isMobile && "User Management"}
                iconPosition="start"
                {...a11yProps(0)}
              />
              <Tab 
                icon={<BillingIcon />}
                label={!isMobile && "Billing & Subscriptions"}
                iconPosition="start"
                {...a11yProps(1)}
              />
              <Tab 
                icon={<ConfigIcon />}
                label={!isMobile && "Pricing Configuration"}
                iconPosition="start"
                {...a11yProps(2)}
              />
              <Tab 
                icon={<AnalyticsIcon />}
                label={!isMobile && "Analytics"}
                iconPosition="start"
                {...a11yProps(3)}
              />
              <Tab 
                icon={<AuditIcon />}
                label={!isMobile && "Audit Log"}
                iconPosition="start"
                {...a11yProps(4)}
              />
            </Tabs>
          </Box>

          <CustomTabPanel value={currentTab} index={0}>
            <UserManagement onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={1}>
            <BillingManagement onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={2}>
            <PricingConfiguration onNotification={showNotification} />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={3}>
            <AdminAnalytics />
          </CustomTabPanel>

          <CustomTabPanel value={currentTab} index={4}>
            <AuditLog />
          </CustomTabPanel>
        </Box>
      </Container>

      <NotificationSnackbar
        open={notification.open}
        onClose={handleNotificationClose}
        message={notification.message}
        description={notification.description}
        severity={notification.severity}
      />
    </>
  );
}

export default AdminDashboard; 